import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { supabase } from '../services/supabaseClient';

interface SyncProgressIndicatorProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  totalFlows: number;
}

export default function SyncProgressIndicator({
  isOpen,
  onClose,
  orgId,
  totalFlows
}: SyncProgressIndicatorProps) {
  const [status, setStatus] = useState<'syncing' | 'processing' | 'completed' | 'error'>('syncing');
  const [message, setMessage] = useState('Checking for updates...');
  const [workerWarning, setWorkerWarning] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !orgId) return;

    let intervalId: NodeJS.Timeout;
    let retryCount = 0;
    
    // Check worker health on mount
    const checkWorkers = async () => {
      try {
        const response = await fetch('/api/system/worker-health');
        if (response.ok) {
          const health = await response.json();
          if (!health.healthy) {
            setWorkerWarning('No active workers detected. Sync may be delayed.');
          }
        }
      } catch (err) {
        console.warn('Could not check worker health:', err);
      }
    };
    checkWorkers();

    const checkProgress = async () => {
      try {
        // Count flows currently being processed
        const { data: processingFlows, count: processingCount } = await supabase
          .from('sf_flow_definitions')
          .select('id', { count: 'exact', head: true })
          .eq('org_id', orgId)
          .in('processing_status', ['pending', 'extracting_metadata', 'generating_summary', 'scoring']);

        const processing = processingCount || 0;
        
        // If we have flows being processed
        if (processing > 0) {
          setStatus('processing');
          setMessage(`Processing ${processing} flow${processing !== 1 ? 's' : ''}...`);
          retryCount = 0; // Reset retry count when we see processing
        } 
        // If no flows are being processed
        else {
          // Increment retry count to track how long nothing has been processing
          retryCount++;
          
          // After 3 checks (6 seconds) with no processing, assume sync is complete
          if (retryCount >= 3) {
            setStatus('completed');
            setMessage('Sync completed successfully!');
            
            // Stop polling immediately
            clearInterval(intervalId);
            
            // Auto-close after 2 seconds
            setTimeout(() => {
              onClose();
            }, 2000);
          } else {
            // Still checking
            setStatus('syncing');
            setMessage('Checking for updates...');
          }
        }
      } catch (err) {
        console.error('Error checking sync progress:', err);
        // On error, close after a few retries
        retryCount++;
        if (retryCount >= 5) {
          setStatus('error');
          setMessage('Unable to check sync status');
          clearInterval(intervalId);
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      }
    };

    // Start checking progress
    checkProgress();
    intervalId = setInterval(checkProgress, 2000); // Check every 2 seconds

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isOpen, orgId, totalFlows, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl transform transition-all">
        <div className="p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            {status === 'syncing' || status === 'processing' ? (
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            ) : status === 'completed' ? (
              <CheckCircleIcon className="h-12 w-12 text-green-600" />
            ) : (
              <XCircleIcon className="h-12 w-12 text-red-600" />
            )}
          </div>

          {/* Title */}
          <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
            {status === 'completed' ? 'Sync Complete!' : 'Syncing Flows'}
          </h3>

          {/* Message */}
          <p className="text-sm text-center text-gray-600 mb-4">{message}</p>
          
          {/* Worker Warning */}
          {workerWarning && status === 'syncing' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <span className="font-medium">Warning:</span> {workerWarning}
              </p>
            </div>
          )}

          {/* Additional Info */}
          {status === 'processing' && (
            <p className="text-xs text-center text-gray-500">
              AI summaries are being generated in the background
            </p>
          )}

          {/* Close Button (only show when completed or error) */}
          {(status === 'completed' || status === 'error') && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}